import { ApiRequestError, type ApiProblem } from "../../platform/api-client";

/** Response body of `POST /api/v1/auth/sessions` on success (F1). */
export interface AuthenticatedSession {
  readonly accountId: string;
  readonly csrfToken: string;
  readonly expiresAt: string;
  readonly version: number;
}

export interface AuthenticateOptions {
  readonly baseUrl?: string;
  readonly fetchImplementation?: typeof fetch;
  readonly signal?: AbortSignal;
}

/**
 * Sign in with credentials.
 *
 * Login is the credential exchange that ISSUES the CSRF token, so it cannot go
 * through the shared `ApiClient` transport — that transport fails closed on any
 * mutation when no CSRF token is available yet, which is every login. The server
 * skips CSRF for this request precisely because no session cookie exists yet
 * (see `registerCsrfProtection`). This helper therefore posts directly, while
 * still mapping failures to the same `ApiRequestError` the rest of the client
 * uses so callers get one error shape.
 */
export async function authenticate(
  credentials: { readonly loginIdentifier: string; readonly password: string },
  options: AuthenticateOptions = {},
): Promise<AuthenticatedSession> {
  const baseUrl = options.baseUrl ?? "/api";
  const request = options.fetchImplementation ?? globalThis.fetch.bind(globalThis);

  const response = await request(`${baseUrl}/v1/auth/sessions`, {
    method: "POST",
    cache: "no-store",
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({
      loginIdentifier: credentials.loginIdentifier,
      password: credentials.password,
    }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (!response.ok) {
    let problem: ApiProblem | null = null;
    if (response.headers.get("content-type")?.includes("json")) {
      try {
        problem = (await response.json()) as ApiProblem;
      } catch {
        problem = null;
      }
    }
    throw new ApiRequestError(response.status, problem);
  }

  return (await response.json()) as AuthenticatedSession;
}
